/**
 * Shared bits of the main tabs and retention features: header with sparks + notifications bell,
 * friendly locked state, playground item card, ticking clock and countdown formatting.
 */
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ENVELOPE_DRAWINGS_TO_FILL, activeChallenge, availableStories, canPlayPlayground, challengeStatus, featureRequirement, isFeatureUnlocked, timeLeft, unopenedLetters, type BonusFeatureId, type PlaygroundItem } from '@sketchling/core';
import { appNow, useAccess, useApp, useProgress } from '../../state/store';
import { wishPath } from '../wish/wish';
import { drawingById, CONTENT } from '../../lib/content';
import { useLoc, useT } from '../../lib/i18n';
import { play } from '../../lib/sfx';
import { DrawingCardArt } from '../../components/DrawingView';
import { CardTitle } from '../../components/CardTitle';
import { Icon, type IconName } from '../../components/icons';
import { Button, ProgressBar, SparkCounter } from '../../components/ui';
import { Scribble } from '../../components/characters';
import { ShelfRow } from '../../components/ShelfRow';
import { JourneyLock } from '../../anim';

/** Re-render every `ms` (countdowns). */
export function useNow(ms = 30_000): Date {
  const [now, setNow] = useState(() => appNow());
  useEffect(() => {
    const iv = setInterval(() => setNow(appNow()), ms);
    return () => clearInterval(iv);
  }, [ms]);
  return now;
}

/** "3d 4h" / "4h 12m" / "12m". */
export function useCountdown() {
  const t = useT();
  return (endsAt: Date, now: Date) => {
    const { days, hours, minutes } = timeLeft(endsAt, now);
    if (days > 0) return t('wc.time.dh', { d: days, h: hours });
    if (hours > 0) return t('wc.time.hm', { h: hours, m: minutes });
    return t('wc.time.m', { m: Math.max(1, minutes) });
  };
}

interface Notice {
  key: string;
  icon: IconName;
  text: string;
  to: string;
}

/** Bell notices: unopened letters, a finished challenge waiting for its chest, new tales. */
export function useNotices(): Notice[] {
  const t = useT();
  const p = useProgress();
  const access = useAccess();
  const cfg = useApp((s) => s.config);
  const now = useNow(60_000);
  return useMemo(() => {
    const out: Notice[] = [];
    const letters = unopenedLetters(p.mailing);
    if (cfg.enableTeacherMailing && letters > 0) out.push({ key: 'letters', icon: 'mail', text: letters === 1 ? t('prog.notice.letterOne') : t('prog.notice.letters', { n: letters }), to: '/letters' });
    if (cfg.enableTeacherMailing && p.mailing.energy >= ENVELOPE_DRAWINGS_TO_FILL && !p.mailing.pending && isFeatureUnlocked(p, 'letters', access.unlockAll))
      out.push({ key: 'envelope', icon: 'mail', text: t('prog.notice.envelope'), to: '/letters' });
    const a = cfg.enableWeeklyChallenges ? activeChallenge(CONTENT.challenges, now) : null;
    if (a && challengeStatus(p, a, now) === 'completed') out.push({ key: 'chest', icon: 'chest', text: t('prog.notice.chest'), to: '/challenges' });
    if (cfg.enableStories && isFeatureUnlocked(p, 'stories', access.unlockAll)) {
      const { available } = availableStories(CONTENT.stories, p.stories.unlockedAt, now, access.premium);
      const fresh = available.filter((e) => !p.stories.seen.includes(e.id)).length;
      if (fresh > 0) out.push({ key: 'tales', icon: 'book', text: fresh === 1 ? t('prog.notice.taleOne') : t('prog.notice.tales', { n: fresh }), to: '/tales' });
    }
    return out;
  }, [p, access, cfg, now, t]);
}

export function MainHeader({ title, children, testId }: { title: ReactNode; children?: ReactNode; testId?: string }) {
  const nav = useNavigate();
  const t = useT();
  const sparks = useProgress().totalSparks;
  const notices = useNotices();
  const [open, setOpen] = useState(false);
  return (
    <header className="topbar main-header" data-testid={testId}>
      <div className="mh-title">{title}</div>
      {children}
      <SparkCounter value={sparks} onClick={() => nav('/sparks')} />
      <div className="bell-wrap">
        <button
          type="button"
          className="icon-btn bell"
          aria-label={t('prog.notice.title')}
          data-testid="bell"
          onClick={() => {
            play(notices.length ? 'drawer' : 'click');
            setOpen((o) => !o);
          }}
        >
          <Icon name="bell" />
          {notices.length > 0 && <span className="badge-dot">{notices.length}</span>}
        </button>
        {open && (
          <>
            <div className="popover-scrim" onClick={() => setOpen(false)} />
            <div className="popover bell-pop" role="menu" data-testid="bell-menu">
              {notices.length === 0 && <p className="muted pop-empty">{t('prog.notice.none')}</p>}
              {notices.map((n) => (
                <button
                  key={n.key}
                  type="button"
                  role="menuitem"
                  className="pop-item"
                  onClick={() => {
                    play('click');
                    setOpen(false);
                    nav(n.to);
                  }}
                >
                  <Icon name={n.icon} />
                  <span>{n.text}</span>
                </button>
              ))}
              <div className="pop-sep" />
              <button type="button" role="menuitem" className="pop-item" onClick={() => nav('/letters')}>
                <Icon name="mail" />
                <span>{t('mail.title')}</span>
              </button>
              <button type="button" role="menuitem" className="pop-item" onClick={() => nav('/challenges')}>
                <Icon name="trophy" />
                <span>{t('wc.title')}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
}

/** Friendly locked state (never a blank page). */
export function LockedState({
  title,
  body,
  sparksNeeded,
  fraction,
  cta,
  onCta,
  testId,
  mood = 'curious',
}: {
  title: string;
  body: string;
  sparksNeeded?: number;
  fraction?: number;
  cta?: string;
  onCta?: () => void;
  testId?: string;
  mood?: 'curious' | 'sleepy' | 'happy';
}) {
  const t = useT();
  return (
    <div className="locked-state" data-testid={testId ?? 'locked-state'}>
      <div className="ls-art">
        <Scribble mood={mood} size={128} className="float" />
        <span className="ls-lock an-wrap">
          <JourneyLock size={44} />
        </span>
      </div>
      <div className="col ls-text">
        <h2>{title}</h2>
        <p className="muted">{body}</p>
        {fraction !== undefined && (
          <div className="ls-progress">
            <ProgressBar value={fraction} tone="gold" />
            {sparksNeeded !== undefined && sparksNeeded > 0 && <small>{t('nav.collectSparks', { n: sparksNeeded })}</small>}
          </div>
        )}
        {cta && onCta && (
          <div>
            <Button onClick={onCta} data-testid="locked-cta">
              {cta}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Locked state for a sparks-gated bonus feature. */
export function FeatureLocked({ feature, title, body }: { feature: BonusFeatureId; title: string; body: string }) {
  const t = useT();
  const nav = useNavigate();
  const p = useProgress();
  const need = featureRequirement(feature);
  return (
    <LockedState
      title={title}
      body={body}
      fraction={Math.min(1, p.totalSparks / need)}
      sparksNeeded={need - p.totalSparks}
      cta={t('prog.keepDrawing')}
      onCta={() => nav(p.ftue.stage === 'playground' ? '/playground' : '/learn')}
      testId={`locked-${feature}`}
    />
  );
}

/** Polaroid card for a playground item (heart, lock, done). */
/** Section of a playground item in a shelf's "See all" sheet (ShelfRow). */
export function sheetGroup(item: PlaygroundItem): string {
  if (item.picture) return 'pictures';
  if (item.crossover) return 'crossovers';
  if (item.remixOf) return 'remix';
  return drawingById(item.drawingId)?.activity === 'doodle' ? 'doodles' : 'lessons';
}

export function ItemCard({ item, from, small, onTap }: { item: PlaygroundItem; from: string; small?: boolean; onTap?: () => void }) {
  const nav = useNavigate();
  const loc = useLoc();
  const t = useT();
  const p = useProgress();
  const access = useAccess();
  const updateProgress = useApp((s) => s.updateProgress);
  const pid = useApp((s) => s.activeProfileId);
  const d = drawingById(item.drawingId);
  const open = canPlayPlayground(item, access) || access.unlockAll;
  const done = p.completedPlaygroundItemIds.includes(item.id);
  const fav = p.favorites.includes(item.id);
  const title = d ? loc(d.title) : item.id;
  return (
    <div className={`item-card${small ? ' small' : ''}${open ? '' : ' is-locked'}`} data-testid={`pg-item-${item.id}`} data-sheet-group={sheetGroup(item)}>
      <button
        type="button"
        className="polaroid"
        aria-label={`${title}${open ? '' : ` (${t('premium')})`}${done ? ` (${t('learn.done')})` : ''}`}
        onClick={() => {
          if (onTap) return onTap();
          if (!open) {
            play('disabled');
            nav(wishPath('playground', item.id, from));
            return;
          }
          play('click');
          nav(`/lesson/${item.miniUnitId}?pg=${item.id}&from=${encodeURIComponent(from)}`);
        }}
      >
        <div className="photo">{d && <DrawingCardArt drawing={d} />}</div>
        <div className="caption">
          <CardTitle text={title} />
        </div>
      </button>
      <button
        type="button"
        className={`heart${fav ? ' on' : ''}`}
        aria-label={fav ? t('pg.unfavorite') : t('pg.favorite')}
        aria-pressed={fav}
        data-testid={`fav-${item.id}`}
        onClick={() => {
          if (!pid) return;
          play(fav ? 'click' : 'sparkle');
          updateProgress(pid, (x) => ({ ...x, favorites: fav ? x.favorites.filter((f) => f !== item.id) : [...x.favorites, item.id], updatedAt: appNow().toISOString() }));
        }}
      >
        <Icon name={fav ? 'heartFill' : 'heart'} size={16} />
      </button>
      {!open && (
        <span className="ic-badge crown" aria-hidden>
          <Icon name="crown" size={14} />
        </span>
      )}
      {done && (
        <span className="ic-badge done" aria-hidden>
          <Icon name="check" size={14} />
        </span>
      )}
    </div>
  );
}

/** Horizontal row with a title: a ShelfRow (peek, arrows, progress, see all). */
export function Row({ title, right, children, testId, kind = 'playground', more }: { title: ReactNode; right?: ReactNode; children: ReactNode; testId?: string; kind?: string; more?: { total: number; items: ReactNode; onOpen?: () => void } }) {
  const hid = useId();
  return (
    <section className="hrow" data-testid={testId}>
      <div className="hrow-head">
        <h3 id={hid}>{title}</h3>
        {right}
      </div>
      <ShelfRow kind={kind} trackClassName="hrow-scroll" labelledBy={hid} sheetTitle={title} more={more}>
        {children}
      </ShelfRow>
    </section>
  );
}
